import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { $ } from '../lib/api';

interface Results {
  customers: Array<{ id: number; nickname: string; realName?: string; currentStage?: string }>;
  contracts: Array<{ id: number; contractCode: string; customerId: number; customerName?: string; amount: string }>;
  orders: Array<{ id: number; orderCode: string; customerId: number; customerName?: string; amount: string }>;
}

/**
 * Topbar search with debounced live results.
 * Triggers on input or ⌘K (focuses the input). Renders a dropdown grouped by entity.
 */
export function GlobalSearch() {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<Results | null>(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const nav = useNavigate();

  // ⌘K / Ctrl+K focuses the input
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Click outside closes dropdown
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Debounced fetch
  useEffect(() => {
    if (!q.trim()) {
      setResults(null);
      return;
    }
    setLoading(true);
    const id = setTimeout(async () => {
      try {
        const r = await $.get<Results>('/search', { q });
        setResults(r);
        setOpen(true);
      } catch {
        setResults(null);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(id);
  }, [q]);

  const total = results ? results.customers.length + results.contracts.length + results.orders.length : 0;

  const go = (url: string) => {
    setOpen(false);
    setQ('');
    nav(url);
  };

  return (
    <div className="topbar-search" ref={wrapperRef} style={{ position: 'relative' }}>
      <svg className="topbar-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
      </svg>
      <input
        ref={inputRef}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results && setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { setOpen(false); (e.target as HTMLInputElement).blur(); }
          if (e.key === 'Enter' && results) {
            // Open the first result
            const first = results.customers[0] ?? results.contracts[0] ?? results.orders[0];
            if (first) {
              if ('contractCode' in (first as any)) go('/contracts');
              else if ('orderCode' in (first as any)) go('/orders');
              else go(`/customers/${(first as any).id}`);
            }
          }
        }}
        placeholder="搜索客户、合同、订单"
      />
      <kbd>⌘K</kbd>

      {open && results && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 8, boxShadow: 'var(--shadow-lg)',
          maxHeight: 480, overflowY: 'auto', zIndex: 50,
          padding: 8,
        }}>
          {loading && <div style={{ padding: 12, color: 'var(--text-3)' }}>搜索中…</div>}
          {!loading && total === 0 && (
            <div style={{ padding: 12, color: 'var(--text-3)', fontSize: 13 }}>未找到匹配的结果</div>
          )}
          {results.customers.length > 0 && (
            <Group title="客户" count={results.customers.length}>
              {results.customers.map((c) => (
                <Item key={`c-${c.id}`} onClick={() => go(`/customers/${c.id}`)}
                  title={c.nickname}
                  sub={[c.realName, c.currentStage].filter(Boolean).join(' · ')}
                />
              ))}
            </Group>
          )}
          {results.contracts.length > 0 && (
            <Group title="合同" count={results.contracts.length}>
              {results.contracts.map((r) => (
                <Item key={`h-${r.id}`} onClick={() => go('/contracts')}
                  title={r.contractCode}
                  sub={`${r.customerName ?? `客户 #${r.customerId}`} · ¥${Number(r.amount).toLocaleString()}`}
                />
              ))}
            </Group>
          )}
          {results.orders.length > 0 && (
            <Group title="订单" count={results.orders.length}>
              {results.orders.map((r) => (
                <Item key={`o-${r.id}`} onClick={() => go('/orders')}
                  title={r.orderCode}
                  sub={`${r.customerName ?? `客户 #${r.customerId}`} · ¥${Number(r.amount).toLocaleString()}`}
                />
              ))}
            </Group>
          )}
        </div>
      )}
    </div>
  );
}

function Group({ title, count, children }: { title: string; count: number; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{
        padding: '6px 8px', fontSize: 11, color: 'var(--text-3)',
        textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600,
      }}>{title} <span style={{ color: 'var(--text-4)' }}>({count})</span></div>
      {children}
    </div>
  );
}

function Item({ title, sub, onClick }: { title: string; sub?: string; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: '8px 10px', borderRadius: 5, cursor: 'pointer',
        transition: 'background 0.12s var(--ease)',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-hover)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <div style={{ fontSize: 13, color: 'var(--text-1)', fontWeight: 500 }}>{title}</div>
      {sub && <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
